import { useField } from 'formik';
import { parseCronExpression } from 'cron-schedule';

import { TimePickerInput } from '@/react/portainer/environments/common/TimeWindowPicker/TimePickerInput';
import { RegistrySelector } from '@/react/portainer/environments/update-schedules/common/RegistrySelector';
import { useRegistries } from '@/react/portainer/registries/queries/useRegistries';
import { RegistryId } from '@/react/portainer/registries/types/registry';
import { BetaAlert } from '@/react/portainer/environments/update-schedules/common/BetaAlert';

import { Option } from '@@/form-components/PortainerSelect';
import { FormSection } from '@@/form-components/FormSection';
import { SwitchField } from '@@/form-components/SwitchField';
import { FormControl } from '@@/form-components/FormControl';
import { Input } from '@@/form-components/Input';
import { HelpLink } from '@@/HelpLink';

import { AutoPatchDatatable } from './AutoPatchDatatable';

export const defaultPatchCron = '0 4 * * *'; // Default cron expression for auto patching

const defaultPortainerRepository = 'portainer/portainer-ee';
const defaultUpdaterRepository = 'portainer/portainer-updater';

export function AutoPatchUpdates() {
  const [{ value }, , { setValue }] = useField<boolean>('autoPatchEnabled');
  const [{ value: cronValue }, , { setValue: setCronValue }] =
    useField<string>('autoPatchCron');

  const [
    { value: customRegistryEnabled },
    ,
    { setValue: setCustomRegistryEnabled },
  ] = useField<boolean>('customRegistryEnabled');
  const [{ value: registryId }, , { setValue: setRegistryId }] = useField<
    number | undefined
  >('registryId');
  const [
    { value: portainerRepository },
    ,
    { setValue: setPortainerRepository },
  ] = useField<string>('portainerRepository');
  const [{ value: updaterRepository }, , { setValue: setUpdaterRepository }] =
    useField<string>('updaterRepository');

  const registriesQuery = useRegistries<Array<Option<RegistryId>>>({
    select: (registries) => {
      const mapped = registries.map((registry) => ({
        value: registry.Id,
        label: registry.Name,
        disabled: registry.Id === 0,
      }));
      if (mapped.length > 1) {
        // Swap first and last elements
        const temp = mapped[0];
        mapped[0] = mapped[mapped.length - 1];
        mapped[mapped.length - 1] = temp;
      }
      return mapped;
    },
  });

  return (
    <FormSection title="Automatic Portainer patch updates">
      <BetaAlert
        className="mb-2"
        message={
          <>
            This is a Beta feature. For more information on Automatic patch
            updates, see the{' '}
            <HelpLink docLink="/admin/settings/general#automatic-portainer-patch-updates">
              Portainer documentation
            </HelpLink>
            .
          </>
        }
      />
      <div className="form-group">
        <div className="col-sm-12 text-muted small mt-2">
          When enabled, patch updates for Portainer will be applied
          automatically to keep Portainer up to date with the latest
          improvements and fixes.
        </div>
        <div className="col-sm-12 mt-4 mb-1">
          <SwitchField
            labelClass="col-sm-3 col-lg-2"
            data-cy="auto-patch-switch"
            data-testid="auto-patch-switch"
            label="Apply patch updates when available"
            checked={value}
            name="toggle_auto_patch_updates"
            onChange={(checked) => setValue(checked)}
          />
        </div>
        {value && (
          <>
            <div className="col-sm-12">
              <div className="flex items-center">
                <span className="space-right control-label !p-0 text-left col-sm-3 col-lg-2">
                  Apply patches at
                </span>
                <TimePickerInput
                  utcTime={cronToTime(cronValue)}
                  onChange={(time: string): void => {
                    setCronValue(timeToCron(time));
                  }}
                />
                <span className="ml-1">UTC</span>
              </div>
              <div className="mb-4 mt-1">
                <SwitchField
                  labelClass="col-sm-3 col-lg-2 mb-2"
                  data-cy="auto-patch-custom-registry-switch"
                  data-testid="auto-patch-custom-registry-switch"
                  label="Use custom registry"
                  checked={customRegistryEnabled}
                  name="toggle_auto_patch_updates"
                  onChange={(checked) => {
                    setCustomRegistryEnabled(checked);
                  }}
                />
              </div>
              {customRegistryEnabled && (
                <>
                  <RegistrySelector
                    required
                    errorMessage={
                      registryId ? '' : 'Selecting a registry is required'
                    }
                    value={registryId || undefined}
                    onChange={(value) => {
                      setRegistryId(value ?? 0);
                    }}
                    registries={registriesQuery.data ?? []}
                    onBlur={() => {}}
                  />
                  <FormControl
                    label="Portainer image repository"
                    required
                    inputId="portainer-repository"
                    errors={
                      !portainerRepository
                        ? 'Portainer image repository is required'
                        : undefined
                    }
                  >
                    <Input
                      name="portainerRepository"
                      id="portainer-repository"
                      placeholder={defaultPortainerRepository}
                      value={portainerRepository ?? ''}
                      onChange={(e) =>
                        setPortainerRepository(e.currentTarget.value)
                      }
                      data-cy="agent-repository-input"
                    />
                  </FormControl>
                  <FormControl
                    label="Updater image repository"
                    required
                    inputId="updater-repository"
                    errors={
                      !updaterRepository
                        ? 'Updater image repository is required'
                        : undefined
                    }
                  >
                    <Input
                      name="updaterRepository"
                      id="updater-repository"
                      placeholder={defaultUpdaterRepository}
                      value={updaterRepository ?? ''}
                      onChange={(e) =>
                        setUpdaterRepository(e.currentTarget.value)
                      }
                      data-cy="update-repository-input"
                    />
                  </FormControl>
                </>
              )}
            </div>
            <AutoPatchDatatable />
          </>
        )}
      </div>
    </FormSection>
  );
}

function cronToTime(cronStr: string): string {
  const defaultTime = '04:00';
  try {
    const cron = parseCronExpression(cronStr || defaultPatchCron);
    return `${String(cron.hours).padStart(2, '0')}:${String(
      cron.minutes
    ).padStart(2, '0')}`;
  } catch (e) {
    return defaultTime;
  }
}

function timeToCron(timeStr: string): string {
  const [hours, minutes] = timeStr.split(':').map(Number);

  return `${minutes} ${hours} * * *`;
}
